import { toast } from "sonner";

// Shopify Storefront API configuration
export const SHOPIFY_API_VERSION = '2025-07';
export const SHOPIFY_STORE_PERMANENT_DOMAIN = 'nott-9286.myshopify.com';
export const SHOPIFY_STOREFRONT_URL = `https://${SHOPIFY_STORE_PERMANENT_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`;
// Storefront API token — public, read-only key (like Supabase anon key).
// Safe to include client-side; scoped to read-only storefront data.
export const SHOPIFY_STOREFRONT_TOKEN = import.meta.env.VITE_SHOPIFY_STOREFRONT_TOKEN ?? '32ee648da88b7127396a72978ec65520';

// ── Retry helpers ──────────────────────────────────────

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 300;

function isRetryable(status: number): boolean {
  return status === 429 || status >= 500;
}

async function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

// ── API Helper ─────────────────────────────────────────

export async function storefrontApiRequest(query: string, variables: Record<string, unknown> = {}) {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10_000);

    try {
      const response = await fetch(SHOPIFY_STOREFRONT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_TOKEN,
        },
        body: JSON.stringify({ query, variables }),
        signal: controller.signal,
      });

      clearTimeout(timeout);

      if (response.status === 402) {
        toast.error("Shopify: Payment required", {
          description: "Your store needs an active billing plan. Visit admin.shopify.com to upgrade.",
        });
        return null;
      }

      if (!response.ok) {
        if (isRetryable(response.status) && attempt < MAX_RETRIES) {
          lastError = new Error(`Shopify API error: ${response.status}`);
          await sleep(BASE_DELAY_MS * Math.pow(3, attempt));
          continue;
        }
        throw new Error(`Shopify API error: ${response.status}`);
      }

      const data = await response.json();

      if (data.errors) {
        throw new Error(`Shopify: ${data.errors.map((e: { message: string }) => e.message).join(', ')}`);
      }

      return data;
    } catch (err) {
      clearTimeout(timeout);
      const isNetwork = err instanceof TypeError || (err instanceof DOMException && err.name === 'AbortError');

      if (isNetwork && attempt < MAX_RETRIES) {
        lastError = err as Error;
        await sleep(BASE_DELAY_MS * Math.pow(3, attempt));
        continue;
      }

      throw err;
    }
  }

  throw lastError ?? new Error('Shopify API request failed after retries');
}
